import React from "react";
import { Navigate, Outlet, useLocation, useOutletContext } from "react-router-dom";
import PropTypes from "prop-types";
import { getUserRoles, clearSession } from "./Utils";

const ProtectedRoute = ({ access_roles, publicAccess = false }) => {
  const location = useLocation();
  // Stay context-transparent: forward any <Outlet context> from a parent layout route
  // (e.g. SchemesTabLayout) so gated child routes can still read useOutletContext().
  const outletContext = useOutletContext();
  const userInfo = localStorage.getItem("DfsWeb.user-info");
  const userDetails = JSON.parse(localStorage.getItem("details"));
  const userRoles = getUserRoles();
  const access_token = localStorage.getItem("DfsWeb.access-token");
  const farmerId = localStorage.getItem("DfsWeb.farmerId");
  const isOnFarmerRegistrationPage =
    location.pathname === `${window.contextPath}/farmer-registration`;

  if (publicAccess && (!userInfo || !access_token)) {
    return <Outlet context={outletContext} />;
  }
  if (!userInfo || !access_token) {
    // Desynced session (e.g. token dropped while other DfsWeb.* keys, incl.
    // isLoggedIn, survived) — clear everything so "/" doesn't read a stale
    // isLoggedIn="true" and bounce straight back to /home forever.
    if (userInfo || access_token || localStorage.getItem("DfsWeb.isLoggedIn")) {
      clearSession();
    }
    const isGoingToHome = location.pathname === `${window.contextPath}/home`;
    return (
      <Navigate
        to={isGoingToHome ? `${window.contextPath || "/"}` : `${window.contextPath}/login`}
        state={{ from: location }}
        replace
      />
    );
  }
  const isOnboardingPending = localStorage.getItem("DfsWeb.onboardingPending") === "true";
  const hasDBTlinked = localStorage.getItem("DfsWeb.hasDBTlinked") === "true";

  if (
    access_token &&
    userRoles.includes("CITIZEN") &&
    (farmerId === "undefined" || !farmerId) &&
    !hasDBTlinked &&
    isOnboardingPending &&
    !isOnFarmerRegistrationPage
  ) {
    const mobile = JSON.parse(userInfo)?.mobileNumber;
    if (!userDetails || userDetails?.mobile !== mobile) localStorage.setItem("details", JSON.stringify({ mobile: mobile }));
    // Incomplete citizen (no farmer Individual yet and onboarding pending) continues the rewamp
    // onboarding form on /login — not the legacy /farmer-registration page.
    return (
      <Navigate
        to={`${window.contextPath}/login`}
        state={{ from: location }}
        replace
      />
    );
  } else if (
    access_roles &&
    !access_roles.some((role) => userRoles.includes(role))
  ) {
    // Authenticated but role lacks access → send to /home, where HomeLanding
    // dispatches the role-appropriate landing (logged-out users never reach
    // here; the branch above returns early). Guard the loop: a role that can't
    // access /home itself (none of COMMON_AUTHENTICATED) would bounce /home→/home
    // forever, so if we're already on /home, fall back to /login.
    const isGoingToHome =
      location.pathname === `${window.contextPath}/home`;
    return (
      <Navigate
        to={`${window.contextPath}/${isGoingToHome ? "login" : "home"}`}
        state={{ from: location }}
        replace
      />
    );
  } else if (access_roles?.some((role) => userRoles.includes(role))) {
    return <Outlet context={outletContext} />;
  }
};

ProtectedRoute.propTypes = {
  access_roles: PropTypes.array,
  publicAccess: PropTypes.bool,
};

export default ProtectedRoute;
